import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { ChevronRightIcon as BreadcrumbChevron } from '@heroicons/react/24/outline';
import { fetchCabWorkbenchMeetings } from '../../../services/cabWorkbenchService';
import type { CabWorkbenchMeeting, CabWorkbenchView } from '../../../types/cabWorkbench';

const WEEK_STARTS_ON = 0 as const; // Sunday — matches ServiceNow screenshot
const EVENT_TEAL = {
  bg: 'bg-teal-100',
  border: 'border-teal-700',
  text: 'text-gray-800',
  activeBtn: 'bg-teal-200 text-teal-900 border-teal-300',
};

function formatEventTime(iso: string): string {
  return format(new Date(iso), 'HH:mm');
}

function formatPopupDateTime(iso: string): string {
  return format(new Date(iso), 'yyyy-MM-dd HH:mm:ss');
}

function meetingsForDay(
  meetings: CabWorkbenchMeeting[],
  day: Date
): CabWorkbenchMeeting[] {
  return meetings.filter((m) => isSameDay(new Date(m.scheduledStart), day));
}

interface PopupState {
  meeting: CabWorkbenchMeeting;
  anchorRect: DOMRect;
}

const CabWorkbench: React.FC = () => {
  const navigate = useNavigate();
  const [view, setView] = useState<CabWorkbenchView>('month');
  const [cursorDate, setCursorDate] = useState(() => new Date());
  const [popup, setPopup] = useState<PopupState | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const range = useMemo(() => {
    if (view === 'day') {
      return {
        start: startOfDay(cursorDate),
        end: endOfDay(cursorDate),
      };
    }
    if (view === 'week') {
      const weekStart = startOfWeek(cursorDate, { weekStartsOn: WEEK_STARTS_ON });
      const weekEnd = endOfWeek(cursorDate, { weekStartsOn: WEEK_STARTS_ON });
      return { start: startOfDay(weekStart), end: endOfDay(weekEnd) };
    }
    // Month and list: full visible month grid (includes leading/trailing days)
    const monthStart = startOfMonth(cursorDate);
    const monthEnd = endOfMonth(cursorDate);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: WEEK_STARTS_ON });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: WEEK_STARTS_ON });
    return { start: startOfDay(gridStart), end: endOfDay(gridEnd) };
  }, [view, cursorDate]);

  const { data: meetings = [], isLoading, isFetching } = useQuery({
    queryKey: [
      'cab-workbench-meetings',
      range.start.toISOString(),
      range.end.toISOString(),
    ],
    queryFn: () =>
      fetchCabWorkbenchMeetings(range.start.toISOString(), range.end.toISOString()),
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const monthDays = useMemo(() => {
    return eachDayOfInterval({ start: range.start, end: range.end });
  }, [range]);

  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(cursorDate, { weekStartsOn: WEEK_STARTS_ON });
    return eachDayOfInterval({
      start: weekStart,
      end: endOfWeek(cursorDate, { weekStartsOn: WEEK_STARTS_ON }),
    });
  }, [cursorDate]);

  useEffect(() => {
    if (!popup) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popupRef.current && !popupRef.current.contains(target)) {
        // Keep open if clicking another event; event handlers set a new popup
        const el = e.target as HTMLElement | null;
        if (el?.closest?.('[data-cab-event]')) return;
        setPopup(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPopup(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [popup]);

  const goPrev = () => {
    setPopup(null);
    if (view === 'day') setCursorDate((d) => addDays(d, -1));
    else if (view === 'week') setCursorDate((d) => addWeeks(d, -1));
    else setCursorDate((d) => addMonths(d, -1));
  };

  const goNext = () => {
    setPopup(null);
    if (view === 'day') setCursorDate((d) => addDays(d, 1));
    else if (view === 'week') setCursorDate((d) => addWeeks(d, 1));
    else setCursorDate((d) => addMonths(d, 1));
  };

  const goToday = () => {
    setPopup(null);
    setCursorDate(new Date());
  };

  const titleLabel = useMemo(() => {
    if (view === 'day') return format(cursorDate, 'MMMM d, yyyy');
    if (view === 'week') {
      const start = startOfWeek(cursorDate, { weekStartsOn: WEEK_STARTS_ON });
      const end = endOfWeek(cursorDate, { weekStartsOn: WEEK_STARTS_ON });
      if (isSameMonth(start, end)) {
        return `${format(start, 'MMM d')} – ${format(end, 'd, yyyy')}`;
      }
      return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
    }
    return format(cursorDate, 'MMMM yyyy');
  }, [view, cursorDate]);

  const openEvent = (meeting: CabWorkbenchMeeting, el: HTMLElement) => {
    setPopup({ meeting, anchorRect: el.getBoundingClientRect() });
  };

  const popupStyle = useMemo((): React.CSSProperties => {
    if (!popup) return {};
    const pad = 8;
    const width = 320;
    const estimatedHeight = 220;
    let left = popup.anchorRect.left;
    let top = popup.anchorRect.bottom + pad;

    if (left + width > window.innerWidth - pad) {
      left = Math.max(pad, window.innerWidth - width - pad);
    }
    if (top + estimatedHeight > window.innerHeight - pad) {
      top = Math.max(pad, popup.anchorRect.top - estimatedHeight - pad);
    }
    return {
      position: 'fixed',
      left,
      top,
      width,
      zIndex: 50,
    };
  }, [popup]);

  const renderEventPill = (meeting: CabWorkbenchMeeting) => (
    <button
      key={meeting.id}
      type="button"
      data-cab-event
      onClick={(e) => {
        e.stopPropagation();
        openEvent(meeting, e.currentTarget);
      }}
      className={`w-full text-left truncate px-1.5 py-0.5 mb-0.5 rounded border text-[11px] leading-tight ${EVENT_TEAL.bg} ${EVENT_TEAL.border} ${EVENT_TEAL.text} hover:brightness-95`}
      title={`${formatEventTime(meeting.scheduledStart)} ${meeting.name}`}
    >
      <span className="font-medium">{formatEventTime(meeting.scheduledStart)}</span>{' '}
      {meeting.name}
    </button>
  );

  const viewButtonClass = (v: CabWorkbenchView) =>
    `px-3 py-1.5 text-sm border border-gray-300 first:rounded-l-md last:rounded-r-md -ml-px first:ml-0 ${
      view === v ? EVENT_TEAL.activeBtn : 'bg-white text-gray-700 hover:bg-gray-50'
    }`;

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      <nav className="mb-2 flex-shrink-0 text-sm text-gray-500" aria-label="Breadcrumb">
        <ol className="flex items-center gap-1 flex-wrap">
          <li>Admin</li>
          <li>
            <BreadcrumbChevron className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li>CAB Meeting Management</li>
          <li>
            <BreadcrumbChevron className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li className="text-gray-900 font-medium">CAB WorkBench</li>
        </ol>
      </nav>

      <div className="bg-white shadow rounded-lg flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 border-b border-gray-200">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={goToday}
              className="p-1.5 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
              aria-label="Calendar"
              title="Today"
            >
              <CalendarDaysIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={goPrev}
              className="p-1.5 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
              aria-label="Previous"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={goNext}
              className="p-1.5 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
              aria-label="Next"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={goToday}
              className="ml-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Today
            </button>
          </div>

          <h1 className="text-lg font-semibold text-gray-900">{titleLabel}</h1>

          <div className="flex items-center">
            <div className="inline-flex" role="group" aria-label="Calendar view">
              <button type="button" className={viewButtonClass('day')} onClick={() => { setView('day'); setPopup(null); }}>
                Day
              </button>
              <button type="button" className={viewButtonClass('week')} onClick={() => { setView('week'); setPopup(null); }}>
                Week
              </button>
              <button type="button" className={viewButtonClass('month')} onClick={() => { setView('month'); setPopup(null); }}>
                Month
              </button>
              <button
                type="button"
                className={viewButtonClass('list')}
                onClick={() => { setView('list'); setPopup(null); }}
                aria-label="List view"
                title="List"
              >
                <Squares2X2Icon className="h-4 w-4 inline" />
              </button>
            </div>
          </div>
        </div>

        {(isLoading || isFetching) && (
          <div className="px-3 py-1.5 text-xs text-gray-500 border-b border-gray-100 bg-gray-50">
            Loading meetings...
          </div>
        )}

        {/* Calendar body */}
        <div className="flex-1 min-h-0 overflow-auto relative">
          {view === 'month' && (
            <div className="h-full min-h-[520px] flex flex-col">
              <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(
                  (d) => (
                    <div
                      key={d}
                      className="px-2 py-2 text-center text-xs font-medium text-gray-600 border-r border-gray-200 last:border-r-0"
                    >
                      {d}
                    </div>
                  )
                )}
              </div>
              <div
                className="flex-1 grid grid-cols-7"
                style={{
                  gridTemplateRows: `repeat(${Math.ceil(monthDays.length / 7)}, minmax(88px, 1fr))`,
                }}
              >
                {monthDays.map((day) => {
                  const inMonth = isSameMonth(day, cursorDate);
                  const dayMeetings = meetingsForDay(meetings, day);
                  const today = isToday(day);
                  return (
                    <div
                      key={day.toISOString()}
                      className={`border-r border-b border-gray-200 p-1 min-h-[88px] overflow-hidden ${
                        today ? 'bg-sky-50' : inMonth ? 'bg-white' : 'bg-gray-50/60'
                      }`}
                    >
                      <div className="flex justify-end mb-0.5">
                        <span
                          className={`text-xs font-medium ${
                            inMonth ? 'text-gray-700' : 'text-gray-400'
                          }`}
                        >
                          {format(day, 'd')}
                        </span>
                      </div>
                      <div className="space-y-0.5 max-h-[calc(100%-1.25rem)] overflow-y-auto">
                        {dayMeetings.map(renderEventPill)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {view === 'week' && (
            <div className="h-full min-h-[400px] flex flex-col">
              <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
                {weekDays.map((day) => (
                  <div
                    key={`hdr-${day.toISOString()}`}
                    className={`px-2 py-2 text-center text-xs font-medium border-r border-gray-200 last:border-r-0 ${
                      isToday(day) ? 'bg-sky-50 text-gray-900' : 'text-gray-600'
                    }`}
                  >
                    {format(day, 'EEE d')}
                  </div>
                ))}
              </div>
              <div className="flex-1 grid grid-cols-7">
                {weekDays.map((day) => {
                  const dayMeetings = meetingsForDay(meetings, day);
                  return (
                    <div
                      key={day.toISOString()}
                      className={`border-r border-b border-gray-200 p-1.5 min-h-[320px] ${
                        isToday(day) ? 'bg-sky-50' : 'bg-white'
                      }`}
                    >
                      {dayMeetings.map(renderEventPill)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {view === 'day' && (
            <div className={`p-4 min-h-[320px] ${isToday(cursorDate) ? 'bg-sky-50/40' : ''}`}>
              <div className="text-sm font-medium text-gray-700 mb-3">
                {format(cursorDate, 'EEEE, MMMM d, yyyy')}
              </div>
              {meetingsForDay(meetings, cursorDate).length === 0 ? (
                <p className="text-sm text-gray-500">No meetings for this day.</p>
              ) : (
                <div className="max-w-xl space-y-1">
                  {meetingsForDay(meetings, cursorDate).map(renderEventPill)}
                </div>
              )}
            </div>
          )}

          {view === 'list' && (
            <div className="p-4">
              {meetings.length === 0 ? (
                <p className="text-sm text-gray-500">No meetings in this period.</p>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Start
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Meeting
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        State
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        CAB Manager
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {meetings.map((m) => (
                      <tr
                        key={m.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        data-cab-event
                        onClick={(e) => openEvent(m, e.currentTarget)}
                      >
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                          {formatPopupDateTime(m.scheduledStart)}
                        </td>
                        <td className="px-3 py-2 text-indigo-700 font-medium">{m.name}</td>
                        <td className="px-3 py-2 text-gray-700">{m.status}</td>
                        <td className="px-3 py-2 text-gray-700">{m.cabManagerName || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {popup && (
        <div
          ref={popupRef}
          style={popupStyle}
          className="bg-white border border-gray-300 rounded-md shadow-lg"
          role="dialog"
          aria-label="Meeting details"
        >
          <div className="flex items-start justify-between gap-2 px-3 pt-3 pb-2">
            <h2 className="text-base font-semibold text-gray-900 leading-snug">
              {popup.meeting.name}
            </h2>
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/admin/cab-meeting-management/cab-workbench/meetings/${popup.meeting.id}`
                )
              }
              className="shrink-0 px-2.5 py-1 text-sm border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50"
            >
              Open
            </button>
          </div>
          <p className="px-3 pb-2 text-sm text-gray-500">{popup.meeting.agendaSummary}</p>
          <div className="border-t border-gray-200 px-3 py-2 space-y-1.5 text-sm">
            <div className="grid grid-cols-[7.5rem_1fr] gap-x-2">
              <span className="text-gray-500">CAB Manager:</span>
              <span className="text-gray-900">{popup.meeting.cabManagerName || '—'}</span>
            </div>
            <div className="grid grid-cols-[7.5rem_1fr] gap-x-2">
              <span className="text-gray-500">Start:</span>
              <span className="text-gray-900">
                {formatPopupDateTime(popup.meeting.scheduledStart)}
              </span>
            </div>
            <div className="grid grid-cols-[7.5rem_1fr] gap-x-2">
              <span className="text-gray-500">End:</span>
              <span className="text-gray-900">
                {formatPopupDateTime(popup.meeting.scheduledEnd)}
              </span>
            </div>
            <div className="grid grid-cols-[7.5rem_1fr] gap-x-2">
              <span className="text-gray-500">State:</span>
              <span className="text-gray-900">{popup.meeting.status}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CabWorkbench;
